import {useEffect, useState} from "react";
import _ from "lodash";
import {useTranslation} from "react-i18next";
import {Box, Button, Stack, Typography} from "@mui/material";
import ApiIcon from "@mui/icons-material/CodeOutlined";
import {useResourceManagement} from "../../hooks/resourceHooks";
import {getCompanyCredentials, rotateCompanyCredentials} from "../../services/companyCredentialsService";
import {OAuthCredentials} from "../../types/domain/oauthCredentials";
import {isResourceLoading, isResourceSucceeded} from "../../utils/resourceUtils";
import {ApiCredentialsCard} from "../users/ApiCredentialsCard";
import {swallowException} from "../../utils/appUtils";
import {CompanyDetailsCard} from "./CompanyDetailsCard";
import {CompanyEditDialog} from "../dialogs/CompanyEditDialog";
import {MainViewLayout} from "../MainViewLayout";
import {CompanyHeader} from "./CompanyHeader";
import {useAccessMarker} from "../../hooks/accessMarkerHooks";
import {ALL_ACCESS_FUNCTIONS} from "../../utils/accessControlUtils";
import {EditIcon} from "../icons/EditIcon";
import {CompanyBrandingCard} from "./CompanyBrandingCard";
import {useCompanyFeatureFlag} from "../../hooks/featureFlagsHook";
import {FeatureFlagEnum} from "../../types/featureFlagsEnum";
import {useCompany, useCompanyHierarchy} from "../../hooks/companiesHooks";
import {queryClient} from "../../utils/queryClient";

export const CompanyProfileView: React.FC = () => {
    const {t} = useTranslation();
    const [companyEditDialogOpened, setCompanyEditDialogOpened] = useState<boolean>(false);
    const company = useCompany();
    const companyHierarchy = useCompanyHierarchy();
    const access = useAccessMarker(ACCESS_FUNCTIONS);
    const isCompanyEligible = useCompanyFeatureFlag(FeatureFlagEnum.whiteLabeling);

    const onCompanyEditDialogClose = (isSuccessful: boolean) => {
        setCompanyEditDialogOpened(false);
        if (isSuccessful) {
            void queryClient.invalidateQueries({queryKey: ["company"]});
            void queryClient.invalidateQueries({queryKey: ["parentCompany"]});
        }
    };

    if (company !== undefined) {
        return (
            <MainViewLayout header={<CompanyHeader basePath="/organization" />}>
                <Stack direction="column" gap="1.5rem">
                    <Stack direction="column">
                        <Stack direction="row" height="4rem" alignItems="center" justifyContent="space-between">
                            <Typography fontSize={20} fontWeight={500} textTransform="uppercase">
                                {t("companies.details_view.details")}
                            </Typography>
                            {access.canEditCompanyDetails && (
                                <Button
                                    startIcon={
                                        <EditIcon
                                            sx={{
                                                stroke: "#303A8C",
                                                width: "1.125rem",
                                                height: "1.125rem",
                                            }}
                                        />
                                    }
                                    onClick={() => setCompanyEditDialogOpened(true)}
                                >
                                    {t("companies.details_view.edit")}
                                </Button>
                            )}
                        </Stack>
                        <Box padding="1.5rem" bgcolor="white">
                            <CompanyDetailsCard
                                company={company?.data}
                                companyHierarchy={companyHierarchy.isSuccess ? companyHierarchy.data : []}
                            />
                        </Box>
                    </Stack>
                    {access.canViewCompanyApiCredentials && company?.data?.isApiEnabled && (
                        <Stack direction="column">
                            <Stack direction="row" height="4rem" alignItems="center" justifyContent="space-between">
                                <Typography fontSize={20} fontWeight={500} textTransform="uppercase">
                                    {t("companies.details_view.api_credentials")}
                                </Typography>
                                <Button
                                    startIcon={<ApiIcon sx={{color: "#303A8C"}} />}
                                    href="/api-v1-swagger"
                                    target="_blank"
                                >
                                    {t("api_credentials.api_swagger_link")}
                                </Button>
                            </Stack>
                            <Box padding="1.5rem" bgcolor="white">
                                <CompanyCredentialsCard companyId={company?.data?.id} />
                            </Box>
                        </Stack>
                    )}
                </Stack>
                {access.canViewCompanyBranding && isCompanyEligible && (
                    <Stack direction="column" gap="1.5rem" marginTop="2rem">
                        <Stack direction="column">
                            <Typography fontSize={20} fontWeight={500} textTransform="uppercase">
                                {t("companies.details_view.branding")}
                            </Typography>
                            <Box padding="1.5rem" bgcolor="white">
                                <CompanyBrandingCard
                                    company={company?.data}
                                    companyHierarchy={companyHierarchy.isSuccess ? companyHierarchy.data : []}
                                />
                            </Box>
                        </Stack>
                    </Stack>
                )}
                {companyEditDialogOpened && company !== undefined && (
                    <CompanyEditDialog
                        mode="edit"
                        company={company?.data}
                        open={companyEditDialogOpened}
                        onClose={onCompanyEditDialogClose}
                    />
                )}
            </MainViewLayout>
        );
    } else {
        return null;
    }
};

const CompanyCredentialsCard: React.FC<{companyId: string | undefined}> = ({companyId}) => {
    const credentialsManagement = useResourceManagement({getCompanyCredentials, rotateCompanyCredentials});
    const {getCompanyCredentials: getCredentials} = credentialsManagement;
    const [credentials, setCredentials] = useState<OAuthCredentials | undefined>(undefined);

    useEffect(() => {
        if (companyId) {
            getCredentials(companyId).catch(swallowException);
        }
    }, [companyId, getCredentials]);

    useEffect(() => {
        if (isResourceSucceeded(credentialsManagement.resource)) {
            setCredentials(credentialsManagement.resource.data);
        }
    }, [credentialsManagement, setCredentials]);

    const onRotateCredentials = () => {
        credentialsManagement.reset();
        if (companyId) {
            credentialsManagement.rotateCompanyCredentials(companyId).catch(swallowException);
        }
    };

    return (
        <ApiCredentialsCard
            isLoading={isResourceLoading(credentialsManagement.resource)}
            credentials={credentials}
            onRotateCredentials={onRotateCredentials}
        />
    );
};

const ACCESS_FUNCTIONS = _.pick(ALL_ACCESS_FUNCTIONS, [
    "canEditCompanyDetails",
    "canViewCompanyApiCredentials",
    "canViewAdminPage",
    "canViewCompanyBranding",
]);
